import { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useIonViewWillEnter } from '@ionic/react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { Device } from '../../../domain/Device';
import {
  MAX_ALERT_CONDITIONS,
  alertableSensors,
  conditionKey,
  parseConditionKey,
  supportsAlerts,
  type Alert,
  type AlertCondition,
  type AlertDirection,
} from '../../../domain/Alert';
import { enabledOfKind } from '../../../domain/Component';
import type { IrrigationSchedule } from '../../../domain/Irrigation';
import type { Lot } from '../../../domain/Lot';

// Una válvula con horario: "avisame si se saltea un riego" vive en su horario.
export interface RiegoNotifyValve {
  device: Device;
  channel: number;
  valveName: string;
  schedule: IrrigationSchedule;
}

// Una fila del formulario: la métrica va como clave (ver conditionKey) y el
// umbral como texto mientras se edita.
// stages: solo para la etapa del lote ("lot:stage").
export interface ConditionDraft {
  key: string;
  direction: AlertDirection;
  threshold: string;
  stages: string[];
}

const toDraft = (c: AlertCondition): ConditionDraft =>
  c.source === 'lot'
    ? { key: conditionKey(c), direction: 'above', threshold: '', stages: c.stages }
    : { key: conditionKey(c), direction: c.direction, threshold: String(c.threshold), stages: [] };

export function useAlertsPage() {
  const { devices: deviceRepo, alerts: alertRepo, irrigation: irrigationRepo, lots: lotRepo } = useRepositories();
  const location = useLocation();

  const [devices, setDevices] = useState<Device[]>([]);
  // Válvulas con horario configurado — la alerta "avisame si se saltea un
  // riego" vive en su IrrigationSchedule (campo notifyOnSkip), no en el
  // modelo Alert, pero se muestra acá para que "prender una alerta" viva en
  // un solo lugar.
  const [riegoNotifyValves, setRiegoNotifyValves] = useState<RiegoNotifyValve[]>([]);
  const [deviceFilter, setDeviceFilter] = useState<string>('all');
  const [alerts, setAlerts] = useState<Alert[]>([]);
  // Lotes, para ofrecer la condición "etapa del lote" en las estaciones que tienen uno.
  const [lots, setLots] = useState<Lot[]>([]);
  const [loading, setLoading] = useState(true);
  // El spinner es solo para la primera carga: al volver a la pantalla (o en
  // cada refresco) los datos se actualizan sin mover la página.
  const loadedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingAlertId, setDeletingAlertId] = useState<string | null>(null);

  // Un solo modal sirve tanto para crear como para editar una alerta.
  // editingAlertId === null → modo "crear" para formDevice.
  const [modalOpen, setModalOpen] = useState(false);
  const [formDevice, setFormDevice] = useState<Device | null>(null);
  const [editingAlertId, setEditingAlertId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formConditions, setFormConditions] = useState<ConditionDraft[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!loadedRef.current) setLoading(true);
    setError(null);
    try {
      const [{ devices: deviceList }, { alerts: alertList }, lotList] = await Promise.all([
        deviceRepo.list(),
        alertRepo.list(),
        lotRepo
          .list()
          .then((r) => r.lots)
          .catch((): Lot[] => []),
      ]);
      setLots(lotList);
      setDevices(deviceList.filter((d) => supportsAlerts(d.components)));
      // Desde el detalle de un dispositivo se llega con ?device=<id> ya filtrado.
      const fromQuery = new URLSearchParams(location.search).get('device');
      if (fromQuery && deviceList.some((d) => d.id === fromQuery)) setDeviceFilter(fromQuery);
      setAlerts(alertList);

      const withValves = deviceList.filter((d) => enabledOfKind(d.components, 'valve').length > 0);
      const valveLists = await Promise.all(
        withValves.map((device) =>
          deviceRepo
            .listValves(device.id)
            .then(({ valves }) =>
              valves.flatMap((v) =>
                v.schedule
                  ? [
                      {
                        device,
                        channel: v.channel,
                        valveName: v.name,
                        schedule: v.schedule,
                      },
                    ]
                  : []
              )
            )
            .catch((): RiegoNotifyValve[] => [])
        )
      );
      setRiegoNotifyValves(valveLists.flat());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar las alertas.');
    } finally {
      loadedRef.current = true;
      setLoading(false);
    }
  };

  useIonViewWillEnter(() => {
    load();
  });

  const handleToggle = async (alert: Alert, enabled: boolean) => {
    setAlerts((prev) => prev.map((a) => (a.id === alert.id ? { ...a, enabled } : a)));
    try {
      // Apagarla mientras está disparada también la cierra del lado del
      // backend (deja de estar "en curso") — reflejamos esa respuesta en vez
      // de solo el enabled optimista, para que el badge desaparezca ya.
      const { alert: updated } = await alertRepo.update(alert.id, { enabled });
      setAlerts((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
    } catch {
      setAlerts((prev) => prev.map((a) => (a.id === alert.id ? { ...a, enabled: !enabled } : a)));
    }
  };

  const handleDelete = async () => {
    if (!deletingAlertId) return;
    try {
      await alertRepo.delete(deletingAlertId);
      setAlerts((prev) => prev.filter((a) => a.id !== deletingAlertId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo eliminar la alerta.');
    }
  };

  const handleToggleNotifyOnSkip = async (target: RiegoNotifyValve, checked: boolean) => {
    const same = (r: RiegoNotifyValve) => r.device.id === target.device.id && r.channel === target.channel;
    setRiegoNotifyValves((prev) =>
      prev.map((r) => (same(r) ? { ...r, schedule: { ...r.schedule, notifyOnSkip: checked } } : r))
    );
    try {
      const { schedule } = target;
      const { schedule: updated } = await irrigationRepo.saveSchedule(target.device.id, target.channel, {
        enabled: schedule.enabled,
        daysOfWeek: schedule.daysOfWeek,
        startTime: schedule.startTime,
        durationSeconds: schedule.durationSeconds,
        flowRateLpm: schedule.flowRateLpm,
        rainSkipEnabled: schedule.rainSkipEnabled,
        rainSkipProbabilityPct: schedule.rainSkipProbabilityPct,
        humidityDeviceId: schedule.humidityDeviceId,
        humidityChannel: schedule.humidityChannel,
        humiditySkipThresholdPct: schedule.humiditySkipThresholdPct,
        notifyOnSkip: checked,
      });
      setRiegoNotifyValves((prev) => prev.map((r) => (same(r) ? { ...r, schedule: updated } : r)));
    } catch {
      setRiegoNotifyValves((prev) =>
        prev.map((r) => (same(r) ? { ...r, schedule: { ...r.schedule, notifyOnSkip: !checked } } : r))
      );
    }
  };

  const openCreate = (device: Device) => {
    const first = alertableSensors(device.components)[0];
    setFormDevice(device);
    setEditingAlertId(null);
    setFormName('');
    setFormConditions([
      {
        key: conditionKey({ source: 'sensor', ...first }),
        direction: 'above',
        threshold: '',
        stages: [],
      },
    ]);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (alert: Alert) => {
    setFormDevice(devices.find((d) => d.id === alert.deviceId) ?? null);
    setEditingAlertId(alert.id);
    setFormName(alert.name ?? '');
    setFormConditions(alert.conditions.map(toDraft));
    setFormError(null);
    setModalOpen(true);
  };

  const applyTemplate = (template: { name: string; conditions: AlertCondition[] }) => {
    setFormName(template.name);
    setFormConditions(template.conditions.map(toDraft));
    setFormError(null);
  };

  const updateCondition = (index: number, patch: Partial<ConditionDraft>) => {
    setFormConditions((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  const addCondition = () => {
    if (!formDevice || formConditions.length >= MAX_ALERT_CONDITIONS) return;
    const keys = alertableSensors(formDevice.components).map((a) => conditionKey({ source: 'sensor', ...a }));
    // Sugiere una métrica que todavía no esté usada, para que "+ Y" ya traiga algo útil.
    const unused = keys.find((k) => !formConditions.some((c) => c.key === k)) ?? keys[0];
    setFormConditions((prev) => [...prev, { key: unused, direction: 'above', threshold: '', stages: [] }]);
  };

  const removeCondition = (index: number) => {
    setFormConditions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!formDevice) return;
    const conditions: AlertCondition[] = [];
    for (let i = 0; i < formConditions.length; i++) {
      const draft = formConditions[i];
      if (draft.key === 'lot:stage') {
        if (draft.stages.length === 0) {
          setFormError(`Elegí al menos una etapa del lote en la condición ${i + 1}.`);
          return;
        }
        conditions.push({ source: 'lot', metric: 'stage', stages: draft.stages });
        continue;
      }
      const threshold = Number(draft.threshold.replace(',', '.'));
      if (!draft.threshold.trim() || Number.isNaN(threshold)) {
        setFormError(`Ingresá un valor numérico en la condición ${i + 1}.`);
        return;
      }
      const { source, metric, channel } = parseConditionKey(draft.key);
      conditions.push(
        (source === 'sensor'
          ? { source, metric, channel, direction: draft.direction, threshold }
          : {
              source,
              metric,
              direction: draft.direction,
              threshold,
            }) as AlertCondition
      );
    }
    if (!conditions.some((c) => c.source === 'sensor')) {
      setFormError('Agregá al menos una condición sobre lo que mide la estación.');
      return;
    }

    setSaving(true);
    setFormError(null);
    try {
      const name = formName.trim() || null;
      if (editingAlertId) {
        const { alert } = await alertRepo.update(editingAlertId, {
          name,
          conditions,
        });
        setAlerts((prev) => prev.map((a) => (a.id === alert.id ? alert : a)));
      } else {
        const { alert } = await alertRepo.create({
          deviceId: formDevice.id,
          name,
          conditions,
        });
        setAlerts((prev) => [...prev, alert]);
      }
      setModalOpen(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'No se pudo guardar la alerta.');
    } finally {
      setSaving(false);
    }
  };

  // El lote de la estación del formulario (si tiene): sus etapas son las opciones.
  const formLot = formDevice?.lotId ? (lots.find((l) => l.id === formDevice.lotId) ?? null) : null;

  const toggleStage = (index: number, stage: string) => {
    setFormConditions((prev) =>
      prev.map((c, i) =>
        i === index ? { ...c, stages: c.stages.includes(stage) ? c.stages.filter((s) => s !== stage) : [...c.stages, stage] } : c
      )
    );
  };

  const visibleDevices = deviceFilter === 'all' ? devices : devices.filter((d) => d.id === deviceFilter);

  return {
    devices,
    deviceFilter,
    setDeviceFilter,
    alerts,
    loading,
    error,
    deletingAlertId,
    setDeletingAlertId,
    modalOpen,
    setModalOpen,
    formDevice,
    editingAlertId,
    formName,
    setFormName,
    formConditions,
    updateCondition,
    toggleStage,
    formLot,
    addCondition,
    removeCondition,
    applyTemplate,
    formError,
    saving,
    handleToggle,
    handleDelete,
    openCreate,
    openEdit,
    handleSubmit,
    visibleDevices,
    riegoNotifyValves,
    handleToggleNotifyOnSkip,
  };
}
